/**
 * Whether the viewer can read an encrypted Space channel.
 *
 * Live notifications and inbox rows stay hidden until a local Cipher is linked
 * for that channel. Missing channel metadata counts as unreadable: a ciphertext
 * payload must not toast before we know which Cipher it uses.
 */

import type {
  CipherCheck,
  InboxItem,
  PublicSpace,
  PublicSpaceChannel,
  PublicSpaceMessage,
} from '@adieuu/shared';
import { resolveLinkedCipherId } from './resolveLinkedCipherId';

export interface ReadableChannelRef {
  id: string;
  spaceId: string;
  categoryId?: string | null;
  cipherCheck?: CipherCheck | null;
}

export interface ReadableSpaceRef {
  id: string;
  e2ee?: boolean;
  cipherCheck?: CipherCheck | null;
}

export function viewerCanReadEncryptedChannel(args: {
  spaceId: string;
  channelId: string;
  space: ReadableSpaceRef | null;
  channel: ReadableChannelRef | null;
  getCipherKey: (localId: string) => unknown;
}): boolean {
  const { channel } = args;
  if (!channel || channel.id !== args.channelId || channel.spaceId !== args.spaceId) {
    return false;
  }
  const localId = resolveLinkedCipherId({
    spaceId: args.spaceId,
    spaceE2ee: !!args.space?.e2ee,
    spaceCipherCheck: args.space?.cipherCheck ?? null,
    channelId: channel.id,
    categoryId: channel.categoryId,
    entityCipherCheck: channel.cipherCheck ?? null,
  });
  if (!localId) return false;
  return args.getCipherKey(localId) != null;
}

function dataString(data: Record<string, unknown>, key: string): string | undefined {
  const value = data[key];
  return typeof value === 'string' ? value : undefined;
}

function channelIsEncrypted(
  explicit: boolean,
  space: ReadableSpaceRef | null,
  channel: ReadableChannelRef | null,
): boolean {
  return explicit || !!space?.e2ee || !!channel?.cipherCheck;
}

/**
 * Inbox rows the viewer cannot decrypt are hidden. Plaintext rows, and rows
 * we cannot classify as encrypted, stay visible.
 */
export function inboxItemIsReadable(
  item: InboxItem,
  args: {
    spaces: readonly ReadableSpaceRef[];
    channels: readonly ReadableChannelRef[];
    getCipherKey: (localId: string) => unknown;
  },
): boolean {
  let spaceId: string | undefined;
  let channelId: string | undefined;
  let explicitEncrypted = false;

  if (item.kind === 'space_mention') {
    spaceId = item.spaceId;
    channelId = item.channelId;
  } else if (
    item.kind === 'notification' &&
    (item.type === 'space_message_mention' || item.type === 'space_message_reply')
  ) {
    spaceId = dataString(item.data, 'spaceId');
    channelId = dataString(item.data, 'channelId');
    explicitEncrypted = item.data.encrypted === true;
  } else {
    return true;
  }

  if (!spaceId || !channelId) return !explicitEncrypted;

  const space = args.spaces.find((candidate) => candidate.id === spaceId) ?? null;
  const channel = args.channels.find((candidate) => candidate.id === channelId) ?? null;
  if (!channelIsEncrypted(explicitEncrypted, space, channel)) return true;

  return viewerCanReadEncryptedChannel({
    spaceId,
    channelId,
    space,
    channel,
    getCipherKey: args.getCipherKey,
  });
}

export function countHiddenUnreadableUnseen(
  items: readonly InboxItem[],
  readableItems: readonly InboxItem[],
  seenAt: string | null,
): number {
  const readableIds = new Set(readableItems.map((item) => item.id));
  let hidden = 0;
  for (const item of items) {
    if (readableIds.has(item.id) || item.read) continue;
    if (seenAt && item.createdAt <= seenAt) continue;
    hidden += 1;
  }
  return hidden;
}

export function messageCiphertextIsUnreadable(
  msg: Pick<PublicSpaceMessage, 'ciphertext'>,
  canRead: ((msg: PublicSpaceMessage) => boolean) | undefined,
): boolean {
  if (!msg.ciphertext) return false;
  if (!canRead) return true;
  return !canRead(msg as PublicSpaceMessage);
}

/** Narrow a public Space/channel down to the fields readability needs. */
export function readableSpaceRef(
  space: Pick<PublicSpace, 'id' | 'e2ee' | 'cipherCheck'> | null | undefined,
): ReadableSpaceRef | null {
  if (!space) return null;
  return { id: space.id, e2ee: space.e2ee, cipherCheck: space.cipherCheck ?? null };
}

export function readableChannelRef(
  channel: Pick<PublicSpaceChannel, 'id' | 'spaceId' | 'categoryId' | 'cipherCheck'>,
): ReadableChannelRef {
  return {
    id: channel.id,
    spaceId: channel.spaceId,
    categoryId: channel.categoryId,
    cipherCheck: channel.cipherCheck ?? null,
  };
}
